// Modelo compartido por la vista previa y el PDF: coordenadas en mm, fuente en pt.
export const PRICE_REPORT_PT_TO_MM = 0.3527;
export const PRICE_REPORT_STRATEGIES = [
  {
    key: "recommended", label: "Recomendado", color: "#15803d",
    border: "#bbf7d0", fill: "#f0fdf4",
    description: ["Equilibrio entre valor de mercado", "y posibilidad de venta."],
  },
  {
    key: "quick", label: "Venta rápida", color: "#168065",
    border: "#c7e8de", fill: "#f5fbf8",
    description: ["Un precio más competitivo", "para priorizar consultas."],
  },
  {
    key: "market", label: "Defendible", color: "#a16207",
    border: "#f4dda7", fill: "#fffcf4",
    description: ["Una referencia respaldada", "por los comparables analizados."],
  },
  {
    key: "premium", label: "Ambicioso", color: "#c24145",
    border: "#f3cdd0", fill: "#fff7f7",
    description: ["Mayor margen de publicación", "para destacar diferenciales."],
  },
];

export function buildEditableReportPrices(options = [], edits = {}, currency = "USD") {
  return PRICE_REPORT_STRATEGIES.map(({ key }) => {
    const sourceValue = options.find((option) => option.key === key)?.value ?? null;
    const edit = edits[key];
    // Una edición pertenece a su estimación y moneda, no a futuros cálculos.
    const value = edit && edit.sourceValue === sourceValue && edit.currency === currency
      ? edit.value
      : sourceValue ?? "";
    return { key, value: String(value) };
  });
}

export function areReportPricesComplete(prices = []) {
  return PRICE_REPORT_STRATEGIES.every(({ key }) => {
    const value = prices.find((price) => price.key === key)?.value;
    return /^\d{1,15}$/.test(String(value ?? "")) && Number(value) > 0;
  });
}

export function formatReportPrice(value, currency = "USD") {
  const number = Number(value);
  return value !== "" && value != null && Number.isFinite(number) && number >= 0
    ? `${currency} ${number.toLocaleString("es-AR")}`
    : `${currency} —`;
}

export function buildPriceReportScene(prices = [], currency = "USD") {
  const elements = [];
  const rect = (x, y, width, height, radius, fill, stroke) =>
    elements.push({ type: "rect", x, y, width, height, radius, fill, stroke });
  const circle = (x, y, radius, fill) =>
    elements.push({ type: "circle", x, y, radius, fill });
  const text = (value, x, y, fontPt, color = "#334155", bold = false, priceKey) =>
    elements.push({ type: "text", value, x, y, fontPt, color, bold, priceKey });

  rect(26, 29, 268, 237, 7, "#f1f5f9");
  rect(26, 28, 268, 237, 7, "#ffffff", "#e2e8f0");
  text("VALORACIÓN DE MERCADO", 38, 41, 10, "#64748b", true);
  ["#16a34a", "#d9a61b", "#d9656b"].forEach((color, index) =>
    circle(271 + index * 5, 39.5, 1.5, color));
  text("¿Qué te parecen estos precios?", 38, 56, 25, "#1e293b", true);
  text("Cuatro alternativas para definir el precio de publicación.", 38, 67, 13, "#64748b");
  rect(38, 77, 244, 0.3, 0, "#e2e8f0");

  const cards = PRICE_REPORT_STRATEGIES.map((strategy, index) => {
    const x = 38 + (index % 2) * 128;
    const y = 87 + Math.floor(index / 2) * 76;
    const value = prices.find((price) => price.key === strategy.key)?.value ?? "";
    const displayValue = formatReportPrice(value, currency);
    const fontPt = displayValue.length > 23 ? 17 : displayValue.length > 18 ? 21 : 28;
    rect(x, y + 1.2, 116, 64, 5, "#f1f5f9");
    rect(x, y, 116, 64, 5, strategy.fill, strategy.border);
    circle(x + 9, y + 13, 1.6, strategy.color);
    text(strategy.label, x + 14, y + 14.5, 15, strategy.color, true);
    text(displayValue, x + 8, y + 35, fontPt, "#1e293b", true, strategy.key);
    strategy.description.forEach((line, lineIndex) =>
      text(line, x + 8, y + 47 + lineIndex * 6, 12, "#64748b"));
    return { ...strategy, value, x: x + 8, y: y + 35, width: 100, fontPt };
  });
  text("Alternativas basadas en los comparables analizados.", 38, 249, 11, "#64748b");
  return { elements, cards };
}

export function drawPriceReport(doc, prices, currency) {
  const { elements } = buildPriceReportScene(prices, currency);
  doc.saveGraphicsState();
  elements.forEach((element) => {
    if (element.type === "text") {
      doc.setFont("helvetica", element.bold ? "bold" : "normal");
      doc.setFontSize(element.fontPt);
      doc.setTextColor(element.color);
      doc.text(element.value, element.x, element.y);
    } else {
      doc.setFillColor(element.fill);
      if (element.type === "circle") {
        doc.circle(element.x, element.y, element.radius, "F");
      } else {
        if (element.stroke) {
          doc.setDrawColor(element.stroke);
          doc.setLineWidth(0.3);
        }
        const style = element.stroke ? "FD" : "F";
        if (element.radius) {
          doc.roundedRect(element.x, element.y, element.width, element.height, element.radius, element.radius, style);
        } else {
          doc.rect(element.x, element.y, element.width, element.height, style);
        }
      }
    }
  });
  doc.restoreGraphicsState();
}
